import { useState, useMemo, useCallback } from 'react';
import { useTheme } from '@emotion/react';
import { useMedia } from '@packlink/utils';
import { IconNames } from '@shipengine/giger-theme';
import { useTranslation } from '@packlink/translation-provider';
import { Spinner, SpinnerSize, Grid, GridChild, Typography, ButtonIcon, ButtonVariant, Input, Icon, InlineNotification, NotificationType } from '@shipengine/giger';
import PudoMap from './PudoMap.js';
import { useLocations } from '../hooks/useLocations.js';
import { useDropOffPoints } from '../hooks/useDropOffPoints.js';
import { getPudoLoadingStyles, getPudoStyles, getPudoGridStyles, getPudoHeaderStyles, getPudoTitleGroupStyles, getPudoToggleStyles, getPudoFilterStyles, getPudoGridListStyles } from './PudoStyles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import List from './list/List.js';

const DEFAULT_TRANSLATIONS = {
  title: 'pudo.header.title',
  subtitle: 'pudo.header.subtitle'
};

/**
 *
 * @description: This component load a list of points and a map from one service and one location. You must use this
 * component inside a LoadScript component of @react-google-maps/api to get access to google maps API. You can pass a
 * mediaQuery param to decide when you want to show collapsed version of component. In README.md you can find the
 * necessary translation keys.
 */
const Pudo = _ref => {
  let {
    className,
    address,
    serviceId,
    mediaQuery,
    translations = DEFAULT_TRANSLATIONS,
    selectedId,
    buyerIcon,
    onConfirm,
    onMapIdle,
    onFilter,
    onFetch
  } = _ref;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const [filterValue, setFilterValue] = useState('');
  const [mapVisible, setMapVisible] = useState(false);
  const [hover, setHover] = useState();
  const [active, setActive] = useState(selectedId);
  const [dropOffPoints, loading] = useDropOffPoints(address, serviceId, onFetch);
  const [buyerMarker, loadingLocations] = useLocations(address, buyerIcon);
  const DEFAULT_BREAKPOINT = `(min-width: ${theme.breakPoints.tablet}px)`;
  const isExpanded = useMedia(mediaQuery !== null && mediaQuery !== void 0 ? mediaQuery : DEFAULT_BREAKPOINT);
  const filteredDropOffPoints = useMemo(() => {
    if (!filterValue) return dropOffPoints;
    return dropOffPoints.filter(element => element.address.toLowerCase().includes(filterValue) || element.businessName.toLowerCase().includes(filterValue));
  }, [dropOffPoints, filterValue]);
  const activePoint = useMemo(() => dropOffPoints.find(dropOff => dropOff.id === active), [active, dropOffPoints]);
  const onFilterChange = _ref2 => {
    let {
      target: {
        value
      }
    } = _ref2;
    setFilterValue(value.toLowerCase());
    onFilter === null || onFilter === void 0 ? void 0 : onFilter();
  };
  const onConfirmFromMap = useCallback(() => {
    activePoint && onConfirm(activePoint);
  }, [activePoint, onConfirm]);
  const onActiveRow = useCallback(dropOffPoint => setActive(dropOffPoint.id), []);
  const onClickToggle = useCallback(() => setMapVisible(prevMapVisible => !prevMapVisible), []);
  const onIdle = useCallback(() => onMapIdle === null || onMapIdle === void 0 ? void 0 : onMapIdle(), [onMapIdle]);
  const responsiveButtonText = t(`pudo.header.show-${mapVisible ? 'list' : 'map'}`);
  return loading || loadingLocations || filteredDropOffPoints === undefined ? jsx("div", {
    css: getPudoLoadingStyles,
    "data-id": "pudo-loading-spinner",
    children: jsx(Spinner, {
      loading: loading,
      size: SpinnerSize.SIZE_SMALL
    })
  }) : jsx("section", {
    className: className,
    css: getPudoStyles,
    children: jsxs(Grid, {
      css: getPudoGridStyles,
      noPadding: true,
      children: [jsxs(GridChild, {
        rowStart: 1,
        colSpan: isExpanded ? 6 : 12,
        children: [jsxs("header", {
          css: getPudoHeaderStyles,
          children: [jsxs("div", {
            css: getPudoTitleGroupStyles,
            children: [jsx(Typography, {
              variant: "heading5",
              children: t(translations.title)
            }), jsx(Typography, {
              variant: "body2",
              component: "div",
              children: t(translations.subtitle)
            })]
          }), !isExpanded && jsx(ButtonIcon, {
            onClick: onClickToggle,
            css: getPudoToggleStyles,
            variant: ButtonVariant.OUTLINED,
            icon: IconNames[mapVisible ? 'LIST' : 'MAP'],
            ariaDescribedby: responsiveButtonText,
            children: responsiveButtonText
          })]
        }), jsx(Input, {
          value: filterValue,
          name: t('pudo.filter'),
          onChange: onFilterChange,
          placeholder: t('pudo.filter'),
          css: getPudoFilterStyles,
          leftContent: jsx(Icon, {
            name: IconNames.SEARCH
          })
        })]
      }), (isExpanded || !mapVisible) && jsx(GridChild, {
        rowStart: 2,
        colSpan: isExpanded ? 6 : 12,
        css: getPudoGridListStyles,
        children: dropOffPoints.length > 0 ? jsx(List, {
          hover: hover,
          dropOffList: filteredDropOffPoints,
          onChangeHover: setHover,
          active: active,
          onActiveRow: onActiveRow,
          onConfirm: onConfirm,
          expanded: isExpanded,
          selectedId: selectedId
        }) : jsx(InlineNotification, {
          type: NotificationType.ALERT,
          title: t('pudo.no-points.title'),
          children: t('pudo.no-points.text')
        })
      }), (isExpanded || mapVisible) && jsx(GridChild, {
        colSpan: isExpanded ? 6 : 12,
        rowStart: isExpanded ? 1 : 2,
        rowSpan: 2,
        colStart: isExpanded ? 7 : 1,
        children: jsx(PudoMap, {
          dropOffPoints: filteredDropOffPoints,
          onActivePoint: setActive,
          onMapIdle: onIdle,
          isExpanded: isExpanded,
          active: activePoint,
          onConfirm: onConfirmFromMap,
          selectedId: selectedId,
          buyerPoint: buyerMarker
        })
      })]
    })
  });
};
Pudo.displayName = 'Pudo';
var Pudo$1 = Pudo;

export { Pudo$1 as default };
//# sourceMappingURL=Pudo.js.map
